import React, { useState } from 'react';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { ConfirmDialog } from '../../reports/edit/EditCommon';
import RecalcPreview from './RecalcPreview';
import { RECALC_MIN_DATE, RECALC_PRESETS, displayDate, presetDate, recalcDateError } from './revenueOps';
import type { RecalcPreset } from './revenueOps';
import { useRecalcPreview } from './useRecalcPreview';

interface RecalcDialogProps {
  open: boolean;
  /** Может смотреть, что изменится (tables.revenue.recalc.preview) */
  canPreview: boolean;
  /** Может запускать пересчет (tables.revenue.recalc.run) */
  canRun: boolean;
  /** Пересчет запускается */
  starting: boolean;
  /** Идет операция над таблицами аптек: новый пересчет сейчас не запустить */
  busy: boolean;
  /** Возвращает true, если пересчет запущен и диалог можно закрыть */
  onStart: (from: string) => Promise<boolean>;
  onClose: () => void;
}

const RecalcDialog: React.FC<RecalcDialogProps> = ({ open, canPreview, canRun, starting, busy, onStart, onClose }) => {
  // Сегодняшняя дата фиксируется при открытии диалога: «будущая дата» не должна меняться, пока он открыт
  const [today] = useState(() => new Date());
  const [preset, setPreset] = useState<RecalcPreset>('year');
  const [custom, setCustom] = useState('');

  const from = presetDate(preset, custom, today);
  const dateError = recalcDateError(from, today);
  const validFrom = dateError ? '' : from;

  const preview = useRecalcPreview(validFrom, canPreview && !busy);
  // Если предпросмотр доступен, запускают пересчет только после того, как увидели, что он изменит
  const previewBlocks = canPreview && (preview.loading || !preview.summary || preview.summary.values === 0);
  const canStart = canRun && !busy && !dateError && !previewBlocks;

  const handleStart = async () => {
    if (!canStart || starting) return;
    if (await onStart(from)) onClose();
  };

  return (
    <ConfirmDialog
      open={open}
      title="Пересчитать историю"
      confirmText={canRun ? 'Пересчитать' : 'Закрыть'}
      loading={starting}
      confirmDisabled={canRun && !canStart}
      maxWidth="md"
      onConfirm={canRun ? handleStart : onClose}
      onClose={onClose}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ color: '#475569', fontSize: '0.88rem' }}>
          Расчетные поля (остатки и поля с формулами) в таблицах аптек будут пересчитаны по текущим формулам. Введенные
          вручную значения не меняются. Пока идет пересчет, работа с данными аптек приостановлена.
        </Typography>

        {busy && (
          <Box sx={{ p: 1.5, border: '1px solid #fde68a', borderRadius: '8px', backgroundColor: '#fffbeb' }}>
            <Typography sx={{ color: '#92400e', fontSize: '0.84rem' }}>
              Идет обслуживание таблиц выручки. Пересчитать историю можно после завершения операции.
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField
            select
            size="small"
            label="Период"
            value={preset}
            onChange={(event) => setPreset(event.target.value as RecalcPreset)}
            sx={{ minWidth: 220 }}
          >
            {RECALC_PRESETS.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
          {preset === 'custom' && (
            <TextField
              size="small"
              type="date"
              label="Начиная с даты"
              value={custom}
              onChange={(event) => setCustom(event.target.value)}
              // Пока дату не начали вводить, ошибку не показываем: кнопка и так недоступна
              error={!!dateError && custom !== ''}
              helperText={custom !== '' && dateError ? dateError : undefined}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: RECALC_MIN_DATE }}
            />
          )}
        </Box>

        {!dateError && (
          <Typography sx={{ color: '#64748b', fontSize: '0.84rem' }}>Пересчет начнется с {displayDate(from)}</Typography>
        )}

        {canPreview ? (
          <RecalcPreview summary={preview.summary} loading={preview.loading} error={preview.error} onRetry={preview.retry} />
        ) : (
          <Typography sx={{ color: '#64748b', fontSize: '0.84rem' }}>
            Предпросмотр недоступен: у вас нет права смотреть, что изменит пересчет.
          </Typography>
        )}
      </Box>
    </ConfirmDialog>
  );
};

export default RecalcDialog;
